import React, { useState, useEffect } from 'react';
import { 
  generateWeeklyCognitiveMetrics, 
  generateMovementPatterns, 
  generateSpatialNavigationMetrics, 
  generateDualTaskPerformances, 
  generateCognitiveWarnings,
  generateCognitiveHealthSummary
} from './utils/mockDataGenerators';

import { calculateCognitiveRiskLevel } from './utils/analysisUtils';
import MovementPatternAnalysis from './components/MovementPatternAnalysis';
import SpatialNavigationTracker from './components/SpatialNavigationTracker';
import DualTaskAssessment from './components/DualTaskAssessment';
import CognitiveWarningSystem from './components/CognitiveWarningSystem';
import CognitiveInsights from './components/CognitiveInsights';
import { Activity, Navigation, Layers, AlertTriangle, Lightbulb } from 'lucide-react';
import { CognitiveWarning } from './interfaces/types';

const CognitiveHealth: React.FC = () => {
  // Generate mock data for all components
  const [cognitiveMetrics, setCognitiveMetrics] = useState(generateWeeklyCognitiveMetrics());
  const [movementPatterns, setMovementPatterns] = useState(generateMovementPatterns(10));
  const [navigationMetrics, setNavigationMetrics] = useState(generateSpatialNavigationMetrics(12));
  const [dualTaskPerformances, setDualTaskPerformances] = useState(generateDualTaskPerformances(8));
  const [warnings, setWarnings] = useState(generateCognitiveWarnings(4));
  const [cognitiveSummary, setCognitiveSummary] = useState(generateCognitiveHealthSummary());
  const [activeTab, setActiveTab] = useState('insights');
  
  // Calculate weekly scores for trend chart
  const weeklyScores = cognitiveMetrics.map(metric => ({
    date: metric.date,
    score: Math.round((metric.navigationScore + metric.memoryScore + metric.executiveFunction) / 3)
  }));
  
  // Function to handle warning acknowledgment
  const handleAcknowledgeWarning = (warningId: string) => {
    setWarnings(prevWarnings => 
      prevWarnings.map(warning => 
        warning.id === warningId ? { ...warning, isAcknowledged: true } : warning
      )
    );
  };
  
  // Recalculate risk level when metrics change
  useEffect(() => {
    const { riskLevel, score, factors } = calculateCognitiveRiskLevel(
      cognitiveMetrics,
      warnings,
      movementPatterns,
      navigationMetrics,
      dualTaskPerformances
    );
    
    setCognitiveSummary(prev => ({
      ...prev,
      overallScore: score,
      riskLevel
    }));
  }, [cognitiveMetrics, warnings, movementPatterns, navigationMetrics, dualTaskPerformances]);
  
  // Custom tab components since we're not using a UI library that provides tabs
  const TabContainer: React.FC<{children: React.ReactNode}> = ({ children }) => (
    <div className="mb-6">{children}</div>
  );
  
  const CustomTabs: React.FC = () => (
    <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
      <TabButton
        active={activeTab === 'insights'}
        onClick={() => setActiveTab('insights')}
        icon={<Lightbulb size={16} />}
        label="Insights"
      />
      <TabButton
        active={activeTab === 'warnings'}
        onClick={() => setActiveTab('warnings')}
        icon={<AlertTriangle size={16} />}
        label="Early Warning"
        badge={warnings.filter(w => !w.isAcknowledged).length}
      />
      <TabButton
        active={activeTab === 'movement'}
        onClick={() => setActiveTab('movement')}
        icon={<Activity size={16} />}
        label="Movement Patterns"
      />
      <TabButton
        active={activeTab === 'navigation'}
        onClick={() => setActiveTab('navigation')}
        icon={<Navigation size={16} />}
        label="Spatial Navigation"
      />
      <TabButton
        active={activeTab === 'dualtask'}
        onClick={() => setActiveTab('dualtask')}
        icon={<Layers size={16} />}
        label="Dual-Task Performance"
      />
    </div>
  );
  
  interface TabButtonProps {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    badge?: number;
  }
  
  const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label, badge }) => (
    <button
      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        active 
          ? 'bg-white text-blue-600 shadow-sm' 
          : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
      }`}
      onClick={onClick}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      {badge ? (
        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none bg-red-100 text-red-800 rounded-full">
          {badge}
        </span>
      ) : null}
    </button>
  );
  
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Lightbulb className="text-blue-500 mr-3" size={28} />
          <div>
            <h2 className="text-2xl font-bold">Cognitive Health Monitoring</h2>
            <p className="text-gray-600">
              Early detection system for cognitive changes through movement analysis
            </p>
          </div>
        </div>
      </div>
      
      <TabContainer>
        <CustomTabs />
        
        {activeTab === 'insights' && (
          <div className="mt-6">
            <CognitiveInsights 
              summary={cognitiveSummary} 
              weeklyScores={weeklyScores} 
            />
          </div>
        )}
        
        {activeTab === 'warnings' && (
          <div className="mt-6">
            <CognitiveWarningSystem 
              warnings={warnings}
              onAcknowledgeWarning={handleAcknowledgeWarning}
            />
          </div>
        )}
        
        {activeTab === 'movement' && (
          <div className="mt-6">
            <MovementPatternAnalysis patterns={movementPatterns} />
          </div>
        )}
        
        {activeTab === 'navigation' && (
          <div className="mt-6">
            <SpatialNavigationTracker metrics={navigationMetrics} />
          </div>
        )}
        
        {activeTab === 'dualtask' && (
          <div className="mt-6">
            <DualTaskAssessment performances={dualTaskPerformances} />
          </div>
        )}
      </TabContainer>
    </div>
  );
};

export default CognitiveHealth; 